import React, { useEffect, useState } from "react";
import { api, formatApiError, mutateOrQueue } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UsersRound } from "lucide-react";

const EMPTY = { name: "", phone: "", email: "", gstin: "", address: "", credit_limit: 0 };

function CustomerModal({ initial, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, credit_limit: Number(form.credit_limit || 0), email: form.email || null };
      if (editing) await api.patch(`/customers/${initial.id}`, payload);
      else await api.post("/customers", payload);
      toast.success(`Customer ${editing ? "updated" : "created"}`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-950/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-zinc-300 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="customer-modal">
        <div className="px-6 py-4 border-b border-zinc-200">
          <div className="label-eyebrow">{editing ? "Edit" : "New"}</div>
          <h3 className="text-xl font-semibold text-zinc-950">{editing ? "Edit customer" : "Add customer"}</h3>
        </div>
        <div className="p-6 space-y-3">
          {[["name","Name *"],["phone","Phone"],["email","Email"],["gstin","GSTIN"],["address","Address"],["credit_limit","Credit limit (₹) — 0 = no limit","number"]].map(([k, lbl, t]) => (
            <div key={k}>
              <label className="label-eyebrow block mb-1.5">{lbl}</label>
              <input data-testid={`customer-${k}`} type={t || "text"} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm uppercase tracking-wider hover:bg-zinc-50">Cancel</button>
          <button data-testid="customer-save-button" disabled={busy} onClick={save} className="px-4 py-2 bg-zinc-950 text-white text-sm uppercase tracking-wider hover:bg-zinc-800 disabled:opacity-50">{busy ? "Saving..." : editing ? "Update" : "Create"}</button>
        </div>
      </div>
    </div>
  );
}

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/customers", { params: q ? { q } : {} }).then(({ data }) => setItems(data));
  useEffect(() => { load(); }, [q]);

  const remove = async (c) => {
    if (!window.confirm(`Delete customer "${c.name}"?`)) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="customers-root">
      <div className="flex items-end justify-between border-b border-zinc-200 pb-6">
        <div>
          <div className="label-eyebrow">CRM</div>
          <h1 className="text-4xl font-bold tracking-tight text-zinc-950">Customers</h1>
          <p className="text-sm text-zinc-500 mt-2">Track GSTIN, credit limits and outstanding balance per customer.</p>
        </div>
        <button data-testid="add-customer-button" onClick={() => { setEdit(null); setOpen(true); }} className="bg-zinc-950 text-white text-sm uppercase tracking-wider font-semibold px-4 py-2.5 hover:bg-zinc-800 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add customer
        </button>
      </div>
      <input data-testid="customers-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / phone / GSTIN..." className="w-full max-w-md bg-white border border-zinc-300 text-sm px-3 py-2.5 font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
      <div className="brutalist-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[11px] border-b border-zinc-200">
              <th className="text-left px-5 py-3">Name</th><th className="text-left px-5 py-3">Phone</th>
              <th className="text-left px-5 py-3">GSTIN</th><th className="text-right px-5 py-3">Credit limit</th>
              <th className="text-right px-5 py-3">Outstanding</th><th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-zinc-500"><UsersRound className="w-6 h-6 mx-auto mb-2 text-zinc-300" />No customers yet.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`customer-row-${c.name}`}>
                <td className="px-5 py-3 text-zinc-950 font-medium">{c.name}</td>
                <td className="px-5 py-3 font-mono text-zinc-700">{c.phone || "—"}</td>
                <td className="px-5 py-3 font-mono text-xs text-zinc-500">{c.gstin || "—"}</td>
                <td className="px-5 py-3 text-right font-mono">{c.credit_limit ? `₹${c.credit_limit.toLocaleString("en-IN")}` : "—"}</td>
                <td className="px-5 py-3 text-right font-mono text-rose-600">{c.outstanding ? `₹${c.outstanding.toLocaleString("en-IN")}` : "—"}</td>
                <td className="px-5 py-3 text-right space-x-1">
                  <button data-testid={`customer-edit-${c.name}`} onClick={() => { setEdit(c); setOpen(true); }} className="p-1.5 border border-zinc-300 hover:bg-zinc-100 inline-flex"><Pencil className="w-3.5 h-3.5" /></button>
                  <button data-testid={`customer-delete-${c.name}`} onClick={() => remove(c)} className="p-1.5 border border-zinc-300 hover:bg-rose-50 hover:border-rose-300 inline-flex"><Trash2 className="w-3.5 h-3.5 text-rose-600" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && <CustomerModal initial={edit} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}
